import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "./api/client";
import { ChevronLeft, ChevronRight, ChevronDown, Grid, Settings, LogOut, LayoutDashboard, ShieldCheck, FileText, Users, UserCog, UserPlus, Download, Building2, Database, TicketCheck, Pencil, Check, X, GripVertical } from "lucide-react";
import "./Dashboard.css";
import { useDomains } from "./hooks";
import { useCurrentDomain } from "./context/DomainContext";
import { componentRegistry } from "./componentRegistry";
import { Star } from "lucide-react";
import { useFavorites } from "./hooks/useFavorites";
import DashboardHome from './DashboardHome';
import ThemeSelector from "./components/ThemeSelector";
import { useTheme } from "./context/ThemeContext";
import { THEME } from "./theme/themeColors";

const SIDEBAR_MIN_WIDTH = 64;
const SIDEBAR_MAX_WIDTH = 320;
const SIDEBAR_DEFAULT = 220;
const COLLAPSED_WIDTH = 64;
const ACTIVE_MENU_KEY = 'activeMenuName';

const iconMap = {
  1: ShieldCheck,
  2: FileText,
  3: Users,
  4: UserCog,
  5: UserPlus,
  6: Download,
  7: Building2,
  8: Database,
  9: FileText,
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { domain, setDomain } = useCurrentDomain();
  const { domains, loading: domainsLoading } = useDomains();
  const { loadUserTheme, resetTheme } = useTheme();

  // Load the logged-in user's theme (cached colour applies instantly, then syncs with API)
  useEffect(() => {
    loadUserTheme();
  }, [loadUserTheme]);

  const sidebarRef = useRef(null);
  const flyoutRef = useRef(null);

const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => { return window.innerWidth <= 768; });  
  const [activeMenuName, setActiveMenuName] = useState(() => { return sessionStorage.getItem(ACTIVE_MENU_KEY) || null });
  const [openMenus, setOpenMenus] = useState({});
  const [flyoutParent, setFlyoutParent] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);

  const [menuTree, setMenuTree] = useState([]);
  const [permissionMap, setPermissionMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mainAreaRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const [isResizing, setIsResizing] = useState(false);
  const [showDomainConfirm, setShowDomainConfirm] = useState(false);
  const [pendingDomain, setPendingDomain] = useState(null);
  const userRoleId = sessionStorage.getItem("userRoleId");
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableMenuTree, setEditableMenuTree] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedPath, setDraggedPath] = useState(null);
  const [dragOverPath, setDragOverPath] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userRole = sessionStorage.getItem("role")?.trim().toLowerCase() || "";
  const isSuperAdmin = userRoleId === "1" || userRole === "superadmin" || userRole === "super admin";
  const isDeveloper = userRoleId === "3" || userRole === "developer" || userRole === "dev";

  useEffect(() => {
    sessionStorage.setItem("API_BASE_URL", process.env.REACT_APP_ADMIN_API_URL);
    sessionStorage.setItem("selectedDomain", "greenyourbills.com");
  }, []);

  useEffect(() => {
    const fetchMenuTree = async () => {
      if (!userRoleId) {
        setError("No role assigned");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`/api/admin/role-menu-access-tree/${userRoleId}`);
        const tree = res.data || [];

        setMenuTree(tree);

        const flat = new Map();
        const defaultOpen = {};
        const flatten = (nodes) => {
          nodes.forEach((node) => {
            flat.set(node.menuId, {
              isAssigned: node.isAssigned,
              permissions: node.permissions || [],
              displayName: node.displayName,
            });
            if (node.children?.length) {
              defaultOpen[node.menuId] = true;
              flatten(node.children);
            }
          });
        };
        flatten(tree);
        setPermissionMap(flat);
        setOpenMenus(defaultOpen);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load permissions");
      } finally {
        setLoading(false);
      }
    };

    fetchMenuTree();
  }, [userRoleId]);

  useEffect(() => {
    if (activeMenuName) {
      sessionStorage.setItem(ACTIVE_MENU_KEY, activeMenuName);
    } else {
      sessionStorage.removeItem(ACTIVE_MENU_KEY);
    }
  }, [activeMenuName]);

  const reorderMenuItem = (fromPath, toPath) => {
    if (fromPath.length !== toPath.length) return;
    const fromParentPath = fromPath.slice(0, -1);
    const toParentPath = toPath.slice(0, -1);
    if (JSON.stringify(fromParentPath) !== JSON.stringify(toParentPath)) return;

    const fromIndex = fromPath[fromPath.length - 1];
    const toIndex = toPath[toPath.length - 1];
    if (fromIndex === toIndex) return;

    setEditableMenuTree((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));

      let parentArray = clone;
      for (let i = 0; i < fromParentPath.length; i++) {
        parentArray = parentArray[fromParentPath[i]].children;
      }

      const [moved] = parentArray.splice(fromIndex, 1);
      parentArray.splice(toIndex, 0, moved);

      return clone;
    });
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const updates = [];
      const collectSeq = (items) => {
        items.forEach((item, idx) => {
          updates.push({ menuId: item.menuId, seq: idx + 1 });
          if (item.children?.length) collectSeq(item.children);
        });
      };
      collectSeq(editableMenuTree);

      await Promise.all(
        updates.map(async ({ menuId, seq }) => {
          const res = await apiClient.get(`/api/admin/menus/${menuId}`);
          const fullMenu = res.data;

          await apiClient.put(`/api/admin/menus/${menuId}`, {
            menuName: fullMenu.menuName,
            parentId: fullMenu.parentId,
            displayName: fullMenu.displayName,
            routingPath: fullMenu.routingPath,
            seq,
            isActive: fullMenu.isActive,
            permissionIds: fullMenu.permissionIds || [],
          });
        })
      );

      setMenuTree((prev) => {
        const reorder = (currentNodes, editedNodes) => {
          return editedNodes.map((edited) => {
            const original = currentNodes.find((n) => n.menuId === edited.menuId);
            if (!original) return edited;
            return {
              ...original,
              children: edited.children?.length
                ? reorder(original.children || [], edited.children)
                : original.children,
            };
          });
        };
        return reorder(prev, editableMenuTree);
      });

      setIsEditMode(false);
    } catch (err) {
      console.error("Failed to save menu order:", err);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleCancelOrder = () => {
    setEditableMenuTree(visibleMenuTree);
    setIsEditMode(false);
  };

  useEffect(() => {
    if (isSidebarCollapsed && isEditMode) {
      handleCancelOrder();
    }
  }, [isSidebarCollapsed]);

  const hasDisplayPermission = useCallback((menuId) => {
    if (!menuId) return false;

    const menuData = permissionMap.get(menuId);
    if (!menuData || !menuData.permissions) return false;

    const displayPerm = menuData.permissions.find(
      (p) => p.permissionId === 1 || p.code === "CanDisplay"
    );

    return displayPerm?.enabled === true;
  }, [permissionMap]);

  const hasUserSettingAccess = useMemo(() => hasDisplayPermission(4), [hasDisplayPermission]);

  const visibleMenuTree = useMemo(() => {
    const filter = (nodes) =>
      nodes
        .map((node) => {
          const canDisplay = hasDisplayPermission(node.menuId);
          if (!canDisplay) return null;
          const filteredChildren = node.children
            ? filter(node.children)
            : [];

          return {
            ...node,
            children: filteredChildren,
          };
        })
        .filter(Boolean);

    return filter(menuTree);
  }, [menuTree, hasDisplayPermission]);

  useEffect(() => {
    setEditableMenuTree(visibleMenuTree);
  }, [visibleMenuTree]);

  const getActiveLabel = () => {
    if (!activeMenuName) return "Dashboard";

    const findLabel = (node) => {
      if (node.menuName === activeMenuName || node.displayName === activeMenuName) {
        return node.displayName || node.menuName;
      }
      if (node.children) {
        for (const child of node.children) {
          const found = findLabel(child);
          if (found) return found;
        }
      }
      return null;
    };

    for (const node of menuTree) {
      const label = findLabel(node);
      if (label) return label;
    }
    return activeMenuName || "Selected Page";
  };

  const getActiveContent = () => {
    if (loading) return <div className="loading">Loading permissions...</div>;

    if (error || visibleMenuTree.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-card">
            <div className="empty-icon">🚀</div>
            <h1>Welcome to Admin Panel</h1>
            <div className="divider" />
            <p className="no-access-message">
              This user does not have any menu access.<br />
              Please contact the administrator.
            </p>
            <div className="support-icon">🎧</div>
          </div>
        </div>
      );
    }

    const userRoleCheck = sessionStorage.getItem("role")?.toLowerCase().trim();
    const isMainDomain = 
        domain?.toLowerCase() === "greenyourbills.com" || 
        domain?.toLowerCase() === "ab.greenyourbills.com";
    const isDeveloper = userRoleCheck === "developer" || userRoleCheck === "dev";

    if (!activeMenuName && (isDeveloper || !isMainDomain)) {
      return (
        <div className="empty-state">
          <div className="empty-card">
            <div className="empty-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 3C18 3 21 6 21 10C21 13 18.5 16 16 17L13 20L11 18L14 15C11 13 9 10 9 7C9 5 11 3 14 3Z"
                  style={{ fill: THEME.primary }}
                />
                <circle cx="14" cy="8" r="1.5" fill="white" />
                <path
                  d="M9 14L7 16M7 16L6 18M7 16L9 17"
                  style={{ stroke: THEME.primary }}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1>Welcome to Admin Panel</h1>
            <div className="divider" />
            <p className="coming-soon">🚀 This feature is coming soon</p>
            <div className="support-icon">🎧</div>
          </div>
        </div>
      );
    }

    if (!activeMenuName) {
      return (
        <DashboardHome
          username={sessionStorage.getItem("username") || "User"}
          domain={domain}
          onViewAllTickets={() => setActiveMenuName("ManageTicket")}
          onViewAllImports={() => setActiveMenuName("GeneralLogs")}
          onQuickAction={(label) => setActiveMenuName(label)}
          onRegisteredUsersClick={() => setActiveMenuName("ManageRegistration")}
        />
      );
    }

    const findMenuNode = (nodes) => {
      for (const node of nodes) {
        if (node.menuName === activeMenuName || node.displayName === activeMenuName) return node;
        if (node.children?.length) {
          const found = findMenuNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    const activeNode = findMenuNode(menuTree);
    const menuName = activeNode?.menuName || activeMenuName;

    if (!menuName) {
      return <div className="p-4 text-danger">Menu node not found for ID: {activeMenuName}</div>;
    }

    const Component = componentRegistry[menuName];

    return Component || (
      <div className="p-5 text-center text-muted">
        <h4>No component registered for: <strong>{menuName}</strong></h4>
      </div>
    );
  };

  const handleParentClick = (item, e) => {
    const name = item.menuName || item.displayName;

    if (!item.children || item.children.length === 0) {
      setActiveMenuName(name);
      setFlyoutParent(null);
      return;
    }

    if (isSidebarCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      setFlyoutTop(rect.top - sidebarRect.top + 2);
      setFlyoutParent(item);
    } else {
      setOpenMenus((prev) => ({ ...prev, [item.menuId]: !prev[item.menuId] }));
    }
  };

  const handleItemClick = (item) => {
    if (item.menuId === 4) {
      handleLogout();
      return;
    }
    const name = item.menuName || item.displayName;
    setActiveMenuName(name);
    setFlyoutParent(null);
  };

  const handleLogout = () => {
    resetTheme(); // login page goes back to the default theme
    localStorage.removeItem("token");
    sessionStorage.clear();
    navigate("/login");
  };

  const performDomainChange = useCallback((newDomain) => {
    setDomain(newDomain);
    sessionStorage.setItem("selectedDomain", newDomain);

    const matched = domains.find((d) => d.name === newDomain);
    if (matched) {
      sessionStorage.setItem("selectedDomainId", String(matched.id));
    } else {
      sessionStorage.removeItem("selectedDomainId");
    }
  }, [setDomain, domains]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
    setFlyoutParent(null);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    setFlyoutParent(null);
  };

  const handleDomainSelect = (e) => {
    const newDomain = e.target.value;
    if (newDomain === domain) return;
    setPendingDomain(newDomain);
    setShowDomainConfirm(true);
  };

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    sidebarRef.current?.classList.add("is-resizing");
    mainAreaRef.current?.classList.add("is-resizing");
    const startX = e.clientX;
    const startWidth = sidebarRef.current
      ? sidebarRef.current.getBoundingClientRect().width
      : sidebarWidth;
    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, startWidth + delta));
      if (sidebarRef.current) sidebarRef.current.style.width = `${newWidth}px`;
      if (mainAreaRef.current) mainAreaRef.current.style.marginLeft = `${newWidth}px`;
    };
    const onMouseUp = (upEvent) => {
      const delta = upEvent.clientX - startX;
      const newWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, startWidth + delta));
      setSidebarWidth(newWidth);
      if (newWidth <= SIDEBAR_MIN_WIDTH + 10) setIsSidebarCollapsed(true);
      else setIsSidebarCollapsed(false);
      if (sidebarRef.current) {
        sidebarRef.current.style.width = "";
        sidebarRef.current.classList.remove("is-resizing");
      }
      if (mainAreaRef.current) {
        mainAreaRef.current.style.marginLeft = "";
        mainAreaRef.current.classList.remove("is-resizing");
      }
      setIsResizing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [sidebarWidth]);

useEffect(() => {
  sessionStorage.setItem("API_BASE_URL", process.env.REACT_APP_ADMIN_API_URL);
}, []);

useEffect(() => {
  if (domainsLoading || !domains?.length) return;
  const savedDomain = sessionStorage.getItem("selectedDomain");
  const isValid = savedDomain && domains.some(d => d.name === savedDomain && !d.isSuspended);
  const fallback = domains.find(d => !d.isSuspended)?.name || domains[0]?.name;
  const resolved = isValid ? savedDomain : fallback;
  sessionStorage.setItem("selectedDomain", resolved);
  setDomain(resolved);
}, [domains, domainsLoading]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target)) {
        setFlyoutParent(null);
      }
    };

    if (flyoutParent) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [flyoutParent]);

  useEffect(() => {
    const width = isSidebarCollapsed ? COLLAPSED_WIDTH : sidebarWidth;
    if (sidebarRef.current) sidebarRef.current.style.width = `${width}px`;
    if (mainAreaRef.current) mainAreaRef.current.style.marginLeft = `${width}px`;
  }, [sidebarWidth, isSidebarCollapsed]);

  useEffect(() => {
    if (domainsLoading || !domains?.length) return;
    const savedDomain = sessionStorage.getItem("selectedDomain");
    const existingId = sessionStorage.getItem("selectedDomainId");
    if (savedDomain && !existingId) {
      const matched = domains.find((d) => d.name === savedDomain);
      if (matched) sessionStorage.setItem("selectedDomainId", String(matched.id));
    }
  }, [domains, domainsLoading]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsSidebarCollapsed(true);
        setIsMobileMenuOpen(false);
      } else {
        setIsSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderMenu = (item, level = 0) => {
    const id = item.menuId || item.id;
    const label = item.displayName || item.label;
    const Icon = iconMap[item.menuId] || LayoutDashboard;
    const hasChildren = item.children?.length > 0;
    const isOpen = openMenus[id] ?? false;
    const isActive = activeMenuName === (item.menuName || item.displayName);
    const menuName = item.menuName || item.displayName;
    const pinned = isFavorite(menuName);
    const isHovered = hoveredMenu === menuName;

    return (
      <div key={id} className="menu-group">
        <div
          className={`menu-item ${level > 0 ? "child" : "parent"} ${isActive ? "active" : ""} ${isOpen ? "open" : ""}`}
          onClick={(e) => handleParentClick(item, e)}
          title={isSidebarCollapsed ? label : undefined}
          style={{ position: "relative" }}
          onMouseEnter={() => setHoveredMenu(menuName)}
          onMouseLeave={() => setHoveredMenu(null)}
        >
          {level === 0 && <Icon size={18} />}
          {!isSidebarCollapsed && <span className="label">{label}</span>}
          {hasChildren && !isSidebarCollapsed && (
            <ChevronDown className={`chevron ${isOpen ? "rotated" : ""}`} size={15} />
          )}
        </div>
        {hasChildren && isOpen && !isSidebarCollapsed && (
          <div className="sub-menu">
            {item.children.map((child) => renderMenu(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderEditableMenu = (items, parentPath = []) => {
    return items.map((item, index) => {
      const Icon = iconMap[item.menuId] || LayoutDashboard;
      const label = item.displayName || item.menuName;
      const hasChildren = item.children?.length > 0;
      const path = [...parentPath, index];
      const pathKey = path.join("-");

      const isDragging = draggedPath?.join("-") === pathKey;
      const isDragOver = dragOverPath?.join("-") === pathKey && !isDragging;

      return (
        <div key={item.menuId} className="menu-group">
          <div
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              setDraggedPath(path);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (
                draggedPath &&
                draggedPath.length === path.length &&
                JSON.stringify(draggedPath.slice(0, -1)) === JSON.stringify(path.slice(0, -1))
              ) {
                setDragOverPath(path);
              }
            }}
            onDragLeave={(e) => {
              e.stopPropagation();
              if (dragOverPath?.join("-") === pathKey) setDragOverPath(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (draggedPath) {
                reorderMenuItem(draggedPath, path);
              }
              setDraggedPath(null);
              setDragOverPath(null);
            }}
            onDragEnd={() => {
              setDraggedPath(null);
              setDragOverPath(null);
            }}
            className={`menu-item ${parentPath.length > 0 ? "child" : "parent"}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              textAlign: "left",
              paddingLeft: parentPath.length > 0 ? `${22 * parentPath.length + 12}px` : undefined,
              opacity: isDragging ? 0.4 : 1,
              borderTop: isDragOver ? "2px solid #ffffff" : "2px solid transparent",
              cursor: "grab",
              transition: "border-color 0.1s, opacity 0.1s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, textAlign: "left", minWidth: 0 }}>
              <GripVertical size={14} style={{ flexShrink: 0, color: "rgba(255,255,255,0.5)" }} />
              {parentPath.length === 0 && <Icon size={18} />}
              <span className="label" style={{ textAlign: "left", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
          </div>
          {hasChildren && (
            <div className="sub-menu">
              {renderEditableMenu(item.children, path)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="dashboard-root">
      <div ref={sidebarRef} className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""} ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div
            className="logo-icon"
            onClick={toggleSidebar}
            style={{ cursor: 'pointer' }}
          >
            <Grid size={18} />
          </div>
          {!isSidebarCollapsed && (
            <div className="logo-text">
              <span className="logo-title">Admin Panel</span>
            </div>
          )}
          <button
            className="collapse-toggle"
            onClick={toggleSidebar}
          >
            {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="menu-group">
            <div
              className={`menu-item parent ${!activeMenuName ? "active" : ""}`}
              onClick={() => setActiveMenuName(null)}
              title={isSidebarCollapsed ? "Dashboard" : undefined}
              style={
                !isSidebarCollapsed
                  ? { display: "flex", alignItems: "center", justifyContent: "space-between" }
                  : undefined
              }
            >
              {!isSidebarCollapsed ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <LayoutDashboard size={20} />
                    <span className="label">Dashboard</span>
                  </div>

                  {isSuperAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isEditMode) {
                          handleCancelOrder();
                        } else {
                          setIsEditMode(true);
                        }
                      }}
                      title={isEditMode ? "Cancel reordering" : "Reorder menu"}
                      style={{
                        background: "none",
                        border: "none",
                        color: "rgba(255,255,255,0.7)",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {isEditMode ? <X size={15} /> : <Pencil size={14} />}
                    </button>
                  )}
                </>
              ) : (
                <LayoutDashboard size={20} />
              )}
            </div>
          </div>

          {isEditMode
            ? renderEditableMenu(editableMenuTree)
            : visibleMenuTree.map((item) => renderMenu(item))}

        </nav>
        {isEditMode && !isSidebarCollapsed && (
          <div style={{ display: "flex", gap: "8px", padding: "10px 12px" }}>
            <button
              onClick={handleSaveOrder}
              disabled={savingOrder}
              style={{
                flex: 1,
                background: THEME.primaryHover, // darker than the sidebar so the button stays visible
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: savingOrder ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <Check size={14} /> {savingOrder ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancelOrder}
              disabled={savingOrder}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <X size={14} /> Cancel
            </button>
          </div>
        )}

        <div className="sidebar-footer" 
          onClick={() => {
            setActiveMenuName("UserInfo");
            setFlyoutParent(null);
          }}
          style={{ 
            cursor: 'pointer' 
          }}
          title={isSidebarCollapsed ? "User Info" : undefined}
        >
          {!isSidebarCollapsed ? (
            <div className="user-card">
              <div className="user-avatar">
                {sessionStorage.getItem("username")?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="user-info">
                <span className="user-name">
                  {sessionStorage.getItem("username") || "User"}
                </span>
                <span className="user-role">
                  {sessionStorage.getItem("role")?.trim().toUpperCase() || "User Role"}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <div className="user-avatar">
                {sessionStorage.getItem("username")?.charAt(0).toUpperCase() || "U"}
              </div>
            </div>
          )}
        </div>
        {!isSidebarCollapsed && (
          <div
            className="sidebar-resize-handle"
            onMouseDown={startResizing}
            title="Drag to resize"
          />
        )}
      </div>

      {isSidebarCollapsed && flyoutParent && flyoutParent.children?.length > 0 && (
        <div ref={flyoutRef} className="flyout-menu" style={{ top: `${flyoutTop}px`, zIndex: 2000 }}>
          {flyoutParent.children
            .filter((child) => hasDisplayPermission(child.menuId))
            .map((child) => (
              <div
                key={child.menuId}
                className={`flyout-item ${activeMenuName === (child.menuName || child.displayName) ? "active" : ""}`}
                onClick={() => handleItemClick(child)}
              >
                {child.displayName || child.menuName}
              </div>
            ))}
          {flyoutParent.menuId === 4 && hasUserSettingAccess && (
            <div
              className="flyout-item"
              onClick={handleLogout}
            >
              <LogOut size={16} style={{ marginRight: "8px" }} />
              Logout
            </div>
          )}
        </div>
      )}

      {isMobileMenuOpen && (
        <div 
          className="sidebar-overlay open" 
          onClick={() => setIsMobileMenuOpen(false)}
          style={{ zIndex: 999 }}
        />
      )}

      <div ref={mainAreaRef} className="main-area" style={{ marginLeft: isMobileMenuOpen ? '280px' : undefined }} >        
        <header className="top-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <button className="hamburger" onClick={toggleMobileMenu} aria-label="Toggle menu">
              ☰
            </button>
            
            <h1 style={{ fontSize: '1.05rem' }}>{getActiveLabel()}</h1>

            {activeMenuName && (() => {
              const pinned = isFavorite(activeMenuName);
              return (
                <button
                  title={pinned ? "Remove from favorites" : "Add to favorites"}
                  onClick={() => pinned ? removeFavorite(activeMenuName) : addFavorite(activeMenuName, getActiveLabel())}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0 }}
                >
                  {!isDeveloper && <Star size={18} fill={pinned ? "#f59e0b" : "none"} stroke={pinned ? "#f59e0b" : "rgba(255,255,255,0.7)"} />}
                </button>
              );
            })()}
          </div>

          <div className="top-right-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <select
              value={domain}
              onChange={handleDomainSelect}
              style={{
                padding: '5px 8px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '13.5px',
                minWidth: '140px',
                maxWidth: '180px',
                backgroundColor: 'white',
                color: '#333',
                cursor: 'pointer'
              }}
            >
              {domainsLoading ? (
                <option>Loading domains...</option>
              ) : domains?.length === 0 ? (
                <option>No domains available</option>
              ) : (
                domains.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name.length > 25 ? d.name.substring(0, 22) + '...' : d.name}
                  </option>
                ))
              )}
            </select>

            <ThemeSelector />

            <button 
              className="btn btn-sm btn-danger px-2 py-1 fw-semibold logout-btn-main"
              onClick={handleLogout}
              style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <LogOut size={18} />
              <span className="logout-text">Logout</span>
            </button>
          </div>
        </header>

        <main className="content-area">{getActiveContent()}</main>
      </div>

      {showDomainConfirm && (
      <div className="modal-overlay">
        <div className="modal-content" 
          style={{ borderRadius: '16px', padding: '20px 24px', maxWidth: '380px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
        >
          <p style={{ marginBottom: '10px', fontSize: '15px', lineHeight: '1.45', fontWeight: 600 }}>
            You are about to switch the domain.
          </p>
          
          <p style={{ marginBottom: '22px', fontSize: '13.5px', color: '#d32f2f', fontWeight: 500, lineHeight: '1.4' }}>
            Please save your data before changing.
          </p>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button 
              onClick={() => { 
                setShowDomainConfirm(false); 
                setPendingDomain(null); 
              }} 
              style={{ padding: '5px 14px', fontSize: '13.5px', border: '1px solid #ccc', borderRadius: '10px', background: '#f8f9fa', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            
            <button 
              onClick={() => { performDomainChange(pendingDomain); setShowDomainConfirm(false); setPendingDomain(null); }} 
              style={{ padding: '5px 14px', fontSize: '13.5px', background: THEME.primary, color: THEME.onPrimary, border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer'
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
      )}

    </div>
  );
};

export default Dashboard;